import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useHotelAdminStore } from '../store'
import { apiClient } from '../services/apiClient'
import { extractErrorMessage } from '../utils/errorHandler'
import { ROUTES } from '../constants/routes'

export default function AdminLoginProtectedPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const setAdmin = useHotelAdminStore((state) => state.setAdmin)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  const invitationCode = searchParams.get('code')
  const isNewAdmin = searchParams.get('new') === 'true'
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  })

  useEffect(() => {
    // Validate code parameter
    if (!invitationCode) {
      setError('Код приглашения не найден. Пожалуйста, используйте ссылку из письма.')
      setTimeout(() => navigate('/'), 3000)
    }
  }, [invitationCode, navigate])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    
    if (!formData.email || !formData.password) {
      setError('Пожалуйста, заполните все поля')
      return
    }

    try {
      setError('')
      setSuccess('')
      setIsLoading(true)
      
      const response = await apiClient.adminLogin(formData.email, formData.password)
      
      if (response.access_token) {
        setAdmin({
          ...response,
          access_token: response.access_token,
        })
        setSuccess('✅ Вход успешен! Перенаправление...')
        
        setTimeout(() => {
          navigate(ROUTES.ADMIN_DASHBOARD)
        }, 1000)
      } else {
        setError('Ошибка: не получен токен от сервера')
      }
    } catch (err: any) {
      setError(extractErrorMessage(err) || 'Ошибка при входе. Проверьте email и пароль.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleNavigateToRegister = () => {
    if (invitationCode) {
      navigate(`/admin-register?code=${invitationCode}`)
    }
  }

  if (!invitationCode) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="w-full max-w-md">
          <div className="bg-white shadow-lg rounded-lg p-8 text-center">
            <h1 className="text-3xl font-bold text-indigo-600 mb-4">HotelCheck</h1>
            <p className="text-red-600 mb-4">Доступ запрещен</p>
            <p className="text-gray-600">Пожалуйста, используйте ссылку приглашения из письма.</p>
            <button
              onClick={() => navigate('/')}
              className="mt-6 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold"
            >
              Вернуться на главную
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="w-full max-w-md">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <h1 className="text-3xl font-bold text-center text-indigo-600 mb-2">HotelCheck</h1>
          <p className="text-center text-gray-600 text-sm mb-8">
            {isNewAdmin ? 'Регистрация администратора' : 'Вход в личный кабинет'}
          </p>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              ⚠️ {error}
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
              ✅ {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="admin@hotel.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                disabled={isLoading}
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Пароль <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                disabled={isLoading}
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold disabled:opacity-50 transition"
            >
              {isLoading ? 'Загрузка...' : 'Войти'}
            </button>
          </form>

          {/* Register Link */}
          {isNewAdmin && (
            <div className="mt-6 text-center border-t pt-6">
              <p className="text-sm text-gray-600 mb-3">
                Нет аккаунта? Перейдите на страницу регистрации:
              </p>
              <button
                onClick={handleNavigateToRegister}
                className="text-indigo-600 hover:text-indigo-700 font-semibold text-sm"
              >
                Зарегистрировать отель →
              </button>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          <p className="font-semibold mb-2">ℹ️ Информация</p>
          <p>Эта страница доступна только по приглашению администратора. Если у вас нет ссылки приглашения, свяжитесь с администратором.</p>
        </div>
      </div>
    </div>
  )
}
